import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import { useFormikContext, getIn } from 'formik';

import Field from 'core/components/form/form.v2';
import { LabelTooltip } from 'core/components/label-tooltip';
import { validateRoofYear } from './FO6.utils';
import { useFO6BusinessLogic } from './useFO6BusinessLogic';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const occupancyUseTypeByStructureCategory = lookupsJson.farm.occupancyUseTypeByStructureCategory;
const buildingClassOptions = lookupsJson.farm.buildingClassOptions;

const BarnOutbuildingFields = ({
  fieldName,
  buildingType,
  initialBuilding,
  isQuonsetBuilding,
  isOpenSidedBuilding,
  disableRiskForm,
  currentHeatingOptions,
  toastRCEWarning,
  ctOptions
}) => {
  const { values, setFieldValue, errors } = useFormikContext();

  // Extract state from farm location address
  const getCurrentState = () => {
    // Extract location index from fieldName path (e.g., "farmLocations[0].risks[1]")
    const fieldParts = fieldName.split('.');
    if (fieldParts[0].includes('farmLocations')) {
      const locationMatch = fieldParts[0].match(/farmLocations\[(\d+)\]/);
      if (locationMatch) {
        const locationIndex = locationMatch[1];
        const locationState = values.farmLocations?.[locationIndex]?.address?.state;
        if (locationState) {
          return locationState;
        }
      }
    }

    // Final fallback to IL for safety
    return 'IL';
  };

  const state = getCurrentState();

  // Get business logic for filtered options
  const { availableBuildingClassOptions } = useFO6BusinessLogic({
    item: values[fieldName] || {},
    fieldName,
    setFieldValue,
    buildingType,
    initialCoverageE: values[fieldName]?.coverages?.coverageE || 0,
    riskType: 'FO6',
    state, // Use actual state from form context
    values
  });

  // State for tracking current field values and validation
  // Use optional chaining and default to undefined for new risks
  const [currentRoofYear, setCurrentRoofYear] = useState(initialBuilding?.roofYear);
  const [currentYearBuilt, setCurrentYearBuilt] = useState(initialBuilding?.yearBuilt);
  const [roofYearError, setRoofYearError] = useState('');

  // Sync local state with Formik values
  useEffect(() => {
    const formikRoofYear = values[fieldName]?.building?.roofYear;
    const formikYearBuilt = values[fieldName]?.building?.yearBuilt;

    if (formikRoofYear !== currentRoofYear) {
      setCurrentRoofYear(formikRoofYear);
    }
    if (formikYearBuilt !== currentYearBuilt) {
      setCurrentYearBuilt(formikYearBuilt);
    }
  }, [values, fieldName, currentRoofYear, currentYearBuilt]);

  // Validate roof year when roofYear or yearBuilt changes
  useEffect(() => {
    const validation = validateRoofYear(currentRoofYear, currentYearBuilt);
    setRoofYearError(validation.isValid ? '' : validation.message);
  }, [currentRoofYear, currentYearBuilt]);
  return (
    <>
      {/* Row 1: Occupancy or use type + Year built in + Roof year */}
      <Field
        xs={4}
        type="select"
        id={`${fieldName}.building.occupancy`}
        name={`${fieldName}.building.occupancy`}
        mode="light"
        label="Occupancy or use type"
        options={occupancyUseTypeByStructureCategory[buildingType]}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.occupancy`)}
        helperText={getIn(errors, `${fieldName}.building.occupancy`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.occupancy) {
            toastRCEWarning();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${fieldName}.building.yearBuilt`}
        name={`${fieldName}.building.yearBuilt`}
        mode="light"
        label="Year built in"
        format={{
          allowNegative: false,
          isAllowed: ({ value }) => value.length <= 4
        }}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.yearBuilt`)}
        helperText={getIn(errors, `${fieldName}.building.yearBuilt`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.yearBuilt) {
            toastRCEWarning();
          }
          setCurrentYearBuilt(val);
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${fieldName}.building.roofYear`}
        name={`${fieldName}.building.roofYear`}
        mode="light"
        label="Roof year"
        format={{
          allowNegative: false,
          isAllowed: ({ value }) => value.length <= 4
        }}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.roofYear`) || !!roofYearError}
        helperText={getIn(errors, `${fieldName}.building.roofYear`) || roofYearError || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.roofYear) {
            toastRCEWarning();
          }
          setCurrentRoofYear(val);
        }}
      />
      {/* Row 2: Total sqft + Quality + Roof type */}
      <Field
        xs={4}
        type="numeric"
        id={`${fieldName}.building.sqFt`}
        name={`${fieldName}.building.sqFt`}
        mode="light"
        label="Total sqft"
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.sqFt`)}
        helperText={getIn(errors, `${fieldName}.building.sqFt`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.sqFt) {
            toastRCEWarning();
          }
        }}
      />
      <Field
        xs={4}
        type="select"
        id={`${fieldName}.building.buildingQuality`}
        name={`${fieldName}.building.buildingQuality`}
        mode="light"
        label="Quality"
        options={bqOptions}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.buildingQuality`)}
        helperText={getIn(errors, `${fieldName}.building.buildingQuality`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.buildingQuality) {
            toastRCEWarning();
          }
        }}
      />
      <Field
        xs={4}
        type="select"
        id={`${fieldName}.building.roofType`}
        name={`${fieldName}.building.roofType`}
        mode="light"
        label="Roof type"
        options={barnRtOptions}
        disabled={disableRiskForm || isQuonsetBuilding}
        fast={false}
        error={!isQuonsetBuilding && !!getIn(errors, `${fieldName}.building.roofType`)}
        helperText={
          isQuonsetBuilding
            ? 'Roof type included in Quonset Building construction'
            : getIn(errors, `${fieldName}.building.roofType`) || undefined
        }
        onChange={(val) => {
          if (val !== initialBuilding?.roofType) {
            toastRCEWarning();
          }
        }}
      />
      {/* Row 3: Construction type + Has open sides + Building class type */}
      <Field
        xs={4}
        type="select"
        id={`${fieldName}.building.constructionType`}
        name={`${fieldName}.building.constructionType`}
        mode="light"
        label="Construction type"
        options={ctOptions}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.constructionType`)}
        helperText={getIn(errors, `${fieldName}.building.constructionType`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.constructionType) {
            toastRCEWarning();
          }
        }}
      />
      <Field
        xs={4}
        type="checkbox"
        id={`${fieldName}.building.structureOpenSides`}
        name={`${fieldName}.building.structureOpenSides`}
        mode="light"
        label="Has open sides?"
        // Note: Checkbox is enabled for all building classes (1, 2, 3) to allow user interaction.
        // Class 1 structures cannot have open sides per rating rules - users will see validation
        // feedback if they check this for class 1, rather than the checkbox being disabled.
        // Open Sided Building: Auto-checked and disabled
        disabled={disableRiskForm || isOpenSidedBuilding}
        helperText={isOpenSidedBuilding ? 'Automatically checked for Open Sided Building' : undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.structureOpenSides) {
            toastRCEWarning();
          }
          // Note: Building class validation happens automatically via Yup schema
          // when structureOpenSides changes - no manual triggering needed
        }}
      />
      <Field
        xs={4}
        type="select"
        id={`${fieldName}.building.buildingClass`}
        name={`${fieldName}.building.buildingClass`}
        mode="light"
        label="Building class type"
        options={availableBuildingClassOptions || buildingClassOptions}
        disabled={disableRiskForm}
        fast={false}
        error={!!getIn(errors, `${fieldName}.building.buildingClass`)}
        helperText={getIn(errors, `${fieldName}.building.buildingClass`) || undefined}
        onChange={(val) => {
          if (val !== initialBuilding?.buildingClass) {
            toastRCEWarning();
          }
        }}
      />
      {/* Row 4: Number of solid fuel burning + Primary heating + Heating units */}
      <Grid item xs={4}>
        <LabelTooltip
          label="Number of solid fuel burning..."
          tooltip={{
            label: 'More Info',
            onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id={`${fieldName}.building.numSolidFuelBurningAppliances`}
            name={`${fieldName}.building.numSolidFuelBurningAppliances`}
            mode="light"
            disabled={disableRiskForm}
            fast={false}
            error={!!getIn(errors, `${fieldName}.building.numSolidFuelBurningAppliances`)}
            helperText={getIn(errors, `${fieldName}.building.numSolidFuelBurningAppliances`) || undefined}
            format={{
              allowNegative: false
            }}
            onChange={(val) => {
              if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                toastRCEWarning();
              }
            }}
          />
        </LabelTooltip>
      </Grid>
      <Grid item xs={4}>
        <Field
          type="select"
          id={`${fieldName}.building.primaryHeatingSource`}
          name={`${fieldName}.building.primaryHeatingSource`}
          mode="light"
          label="Primary heating"
          options={currentHeatingOptions}
          disabled={disableRiskForm}
          fast={false}
          error={!!getIn(errors, `${fieldName}.building.primaryHeatingSource`)}
          helperText={getIn(errors, `${fieldName}.building.primaryHeatingSource`) || undefined}
          onChange={(val) => {
            if (val !== initialBuilding?.primaryHeatingSource) {
              toastRCEWarning();
            }
          }}
        />
      </Grid>
    </>
  );
};

BarnOutbuildingFields.propTypes = {
  fieldName: PropTypes.string.isRequired,
  buildingType: PropTypes.string.isRequired,
  initialBuilding: PropTypes.object.isRequired,
  isQuonsetBuilding: PropTypes.bool.isRequired,
  isOpenSidedBuilding: PropTypes.bool.isRequired,
  disableRiskForm: PropTypes.bool.isRequired,
  currentHeatingOptions: PropTypes.array.isRequired,
  toastRCEWarning: PropTypes.func.isRequired,
  ctOptions: PropTypes.array.isRequired
};

export default BarnOutbuildingFields;
